import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs'

// Prime React Components
import { classNames } from 'primereact/utils';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';

// Needed URLS
import { GET_USERS, ADD_USER, EDIT_USER, DELETE_USER } from '../constants/URLS';

// Needed common templates
import { timeStampTemplate } from '../constants/commonTemplates'

const Users = (props) => {
	// PAGE REFS
	const toast = useRef(null);
    const dt = useRef(null);

	// CONSTANTS
	// This could become dymanic at some point, maybe when we add a 2nd real section
	const sectionItems = [
		{label: 'Addesso Admin', value: 'addesso'},
		{label: 'ANHI', value: 'anhi'},
		{label: 'Testing', value: 'testing'},
		{label: 'Neurelis', value: 'neurelis'},
		{label: 'Neurelis Micro', value: 'neurelismicro'}
	];

	const sectionItemLookup = sectionItems.reduce((acc,cur) => {
		acc[cur.value] = cur.label
		return acc
	},{})

	// default user details
	const emptyUser = {
        userID: null,
        firstName: '',
        lastName: '',
        email: '',
        sections: [],
        password: '',
		ipAddress: ''
    };

	// STATE VARIABLES
	const [users, setUsers] = useState(null);
	const [globalFilter, setGlobalFilter] = useState(null);
	const [user, setUser] = useState(emptyUser);
    const [userDialog, setUserDialog] = useState(false);
	const [selectedUsers, setSelectedUsers] = useState(null);
	const [deleteUsersDialog, setDeleteUsersDialog] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [deleteUserDialog, setDeleteUserDialog] = useState(false);

	// USE EFFECTS HOOKS
	useEffect(() => {
        fncGetUsers() //On component load, get the users from DB
    }, []); 

	// ACTION / XHR FUNCTIONS
	const fncGetUsers = async (data) => {
		let _users = await axios({method: 'get', url: GET_USERS})
		setUsers(_users.data.results.map(cur => {
			return {
				...cur,
				sections: cur.sections.split(',').map(curS => curS.trim())
			}
		}))
	};

	const deleteUser = async () => {
		let deleteResults = await axios({
			method: 'post', 
			url: DELETE_USER,
			data: {...user}
		})
		if (deleteResults.data.success) {
			let _users = users.filter(val => val.userID !== user.userID);
			setUsers(_users);
			setDeleteUserDialog(false);
			setUser(emptyUser);
			
			toast.current.show({ severity: 'success', summary: 'Successful', detail: 'User Deleted', life: 3000 });
		} else {
			toast.current.show({ severity: 'error', summary: 'Error', detail: deleteResults.data.message, life: 3000 });
		}
    }

	const saveUser = async () => {
        setSubmitted(true);
		let errors = []
		if (!user.firstName.trim()) errors.push('firstName')
		if (!user.lastName.trim()) errors.push('lastName')
		if (!user.email.trim()) errors.push('email')
		if (!user.ipAddress.trim()) errors.push('ipAddress')
		if (!user.userID && !user.password.trim()) errors.push('password')
		if (!user.sections.length) errors.push('sections')
		

		if (errors.length > 0) {
			return  toast.current.show({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.', life: 3000 });
		}

		let _users = [...users];
		let _user = {...user};
		if (user.userID) {
			let editResults = await axios({
				method: 'post', 
				url: EDIT_USER,
				data: {..._user}
			})
			if (editResults.data.success) {
				const index = findIndexById(user.userID);
                _users[index] = _user;
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'User Updated', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: editResults.data.message, life: 3000 });
			}
		}
		else {
			let addResults = await axios({
				method: 'post', 
				url: ADD_USER,
				data: {..._user}
			})
			
			if (addResults.data.success) {
				_user.userID = addResults.data.userID;
				_user.timeStamp = dayjs().format('MMMM DD, YYYY hh:mm:ss')
				_users.push(_user);
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'User Created', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: addResults.data.message, life: 3000 });
			}
			
		}
		setSubmitted(false);
		setUserDialog(false);
		setUsers(_users);
		setUser(emptyUser);
    }

	// OTHER FUNCTIONS
	const openNew = () => {
        setUser(emptyUser);
        setSubmitted(false);
        setUserDialog(true);
    }

	const confirmDeleteSelected = () => {
        setDeleteUsersDialog(true);
    }

	const hideDialog = () => {
        setSubmitted(false);
        setUserDialog(false);
    }

	const editUser = (user) => {
        setUser({...user});
        setUserDialog(true);
    }

	const confirmDeleteUser = (user) => {
        setUser(user);
        setDeleteUserDialog(true);
    }

	const findIndexById = (userID) => {
        let index = -1;
        for (let i = 0; i < users.length; i++) {
            if (users[i].userID === userID) {
                index = i;
                break;
            }
        }

        return index;
    }

	const onInputChange = (e, name) => {
		const val = (e.target && e.target.value) || '';
        let _user = {...user};
        _user[`${name}`] = val;

        setUser(_user);
    }

	const hideDeleteUserDialog = () => {
        setDeleteUserDialog(false);
    }

	// TABLE COLUMN TEMPLATES (table provides rowdata and column info, can draw how needed)
	const actionBodyTemplate = (rowData) => {
        return (
            <>
                <Button id={rowData.userID + "_edit"} icon="pi pi-pencil" className="p-button-rounded p-button-success mr-2" title="Edit Details" onClick={() => editUser(rowData)} />
				<Button id={rowData.userID + "_delete"}icon="pi pi-trash" className="p-button-rounded p-button-warning" title="Delete User"  onClick={() => confirmDeleteUser(rowData)} />
            </>
        );
    }

	const sectionsTemplate = (rowData) => {
		return (
			rowData.sections.map((cur) => {
				return (
					<div>
						{sectionItemLookup[cur]}
					</div>
				)
			})
		)
	}

	const nameTemplate = (rowData) => {
		return (
			<div>{rowData.firstName} {rowData.lastName}</div>
		)
	}

	// OTHER TEMPLATES (JSX)
	const leftToolbarTemplate = () => {
		return <Button label="Add New User" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
	}

	const userDialogFooter = (
        <>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label="Save" icon="pi pi-check" className="p-button-text" onClick={saveUser} />
        </>
    );

	const deleteUserDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteUserDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteUser} />
        </>
    );

	// DRAW OUT THE HTML (table plus modals/diaglogs)
	return (
	   <div className="users">
			<Toast ref={toast} />
	       	<div className="card">
				<Toolbar className="mb-4" left={leftToolbarTemplate} ></Toolbar>
		  		<DataTable ref={dt} value={users} sortField="userID" sortOrder={-1}
					dataKey="userID" paginator rows={10} rowsPerPageOptions={[5, 10, 25]} 
					paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
					currentPageReportTemplate="Showing {first} to {last} of {totalRecords} users"
					globalFilter={globalFilter} responsiveLayout="stack">
						<Column field="timeStamp" header="Created" body={timeStampTemplate} sortable style={{ width: '20%', verticalAlign:'top' }}></Column>
						<Column field="userID" header="User ID" sortable style={{ width: '10%',verticalAlign:'top' }}></Column>
						<Column field="firstName" header="Name" body={nameTemplate} sortable style={{ width: '20%', verticalAlign:'top' }}></Column>
						<Column field="email" header="Email Address" sortable style={{ width: '20%', verticalAlign:'top' }}></Column>
						<Column field="sections" body={sectionsTemplate} header="Sections" style={{ width: '15%', verticalAlign:'top' }}></Column>
						<Column body={actionBodyTemplate} exportable={false} style={{ width: '15%', verticalAlign:'top' }}></Column>
				</DataTable>
	       	</div>

		   <Dialog visible={userDialog} style={{ width: '850px' }} header="User Details" modal className="p-fluid" footer={userDialogFooter} onHide={hideDialog}>
                <div className="flex flex-column">
					<div className="flex">
						<div className="field" style={{width:'33%'}}>
							<label htmlFor="firstName">First Name</label>
							<InputText id="firstName" value={user.firstName} onChange={(e) => onInputChange(e, 'firstName')} required autoFocus className={classNames({ 'p-invalid': submitted && !user.firstName })} />
							{submitted && !user.firstName && <small className="p-error">First Name is required.</small>}
						</div>
						<div className="field ml-2" style={{width:'33%'}}>
							<label htmlFor="lastName">Last Name</label>
							<InputText id="lastName" value={user.lastName} onChange={(e) => onInputChange(e, 'lastName')} required  className={classNames({ 'p-invalid': submitted && !user.lastName })} />
							{submitted && !user.lastName && <small className="p-error">Last Name is required.</small>}
						</div>
						
							<div className="field ml-2" style={{width:'33%'}}>
								<label htmlFor="password">Password</label>
								<InputText id="password" type="password" value={user.password} onChange={(e) => onInputChange(e, 'password')} required  className={classNames({ 'p-invalid': submitted && !user.userID && !user.password })} />
								{submitted && !user.userID && !user.password && <small className="p-error">Password is required.</small>}
								{user.userID && <span className="smallMessage"> (Only enter if you wish to update)</span>}
							</div>
						
					</div>
					<div className="flex mt-2">
						<div className="field" style={{width:'66%'}}>
							<label htmlFor="email">Email Address</label>
							<InputText id="email" value={user.email} onChange={(e) => onInputChange(e, 'email')} required  className={classNames({ 'p-invalid': submitted && !user.email })} />
							{submitted && !user.email && <small className="p-error">Email is required.</small>}
						</div>
						<div className="field ml-2" style={{width:'33%'}}>
							<label htmlFor="sections">Sections</label>
							<MultiSelect id="sections" value={user.sections} options={sectionItems} onChange={(e) => onInputChange(e, 'sections')} required  className={classNames({ 'p-invalid': submitted && !user.sections.length })}  />
							{submitted && !user.sections.length && <small className="p-error">Sections is required.</small>}
						</div>
					</div>
					<div className="flex mt-2">
						<div className="field" style={{width:'66%'}}>
							<label htmlFor="ipAddress">IP Address</label>
							<InputText id="ipAddress" value={user.ipAddress} onChange={(e) => onInputChange(e, 'ipAddress')} required  className={classNames({ 'p-invalid': submitted && !user.ipAddress })} />
							{submitted && !user.ipAddress && <small className="p-error">IP is required.</small>}
						</div>
						
					</div>
				</div>
            </Dialog>

			<Dialog visible={deleteUserDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteUserDialogFooter} onHide={hideDeleteUserDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem'}} />
                    {user && <span>Are you sure you want to delete <b>{user.email}</b>?</span>}
                </div>
            </Dialog>
	   </div>
	);
}

export default Users;